import { useState } from "react";
import { ArrowSquareOut, CheckCircle, FilePdf, Receipt, WarningCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useNoteDetail } from "@/hooks/useNoteDetail";

const STATUS_STYLES = {
  performing: "bg-[hsl(var(--ploy-pos-soft))] text-[hsl(var(--ploy-pos))]",
  active: "bg-[hsl(var(--ploy-pos-soft))] text-[hsl(var(--ploy-pos))]",
  current: "bg-[hsl(var(--ploy-pos-soft))] text-[hsl(var(--ploy-pos))]",
  default: "bg-[hsl(var(--ploy-neutral-soft))] text-[hsl(var(--ploy-neutral))]",
};

const ATTENTION_STYLES = {
  none: "bg-[hsl(var(--ploy-neutral-soft))] text-[hsl(var(--ploy-neutral))]",
  late_15: "bg-[hsl(var(--ploy-attn-soft))] text-[hsl(var(--ploy-attn))]",
  late_30: "bg-[hsl(var(--ploy-attn-soft))] text-[hsl(var(--ploy-attn))]",
  late_90: "bg-[hsl(var(--ploy-rust-soft))] text-[hsl(var(--ploy-rust))]",
  balloon_soon: "bg-[hsl(var(--ploy-attn-soft))] text-[hsl(var(--ploy-attn))]",
  past_maturity: "bg-[hsl(var(--ploy-rust-soft))] text-[hsl(var(--ploy-rust))]",
};

const ATTENTION_LABELS = {
  none: "No attention flags",
  late_15: "15+ days late",
  late_30: "30+ days late",
  late_90: "90+ days late",
  balloon_soon: "Balloon soon",
  past_maturity: "Past maturity",
};

const fmtDate = (value) => {
  if (!value) return "-";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const fmtTimestamp = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const labelize = (value) => {
  if (!value) return "-";
  return String(value)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const fmtRate = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const pct = Number(value) * 100;
  if (Number.isNaN(pct)) return "-";
  return `${pct.toFixed(2)}%`;
};

const fmtRatioPct = (value) => {
  const pct = Number(value || 0) * 100;
  if (Number.isNaN(pct)) return "0.00%";
  return `${pct.toFixed(2)}%`;
};

const sortByDateDesc = (items, field, fallbackField) => [...(items || [])].sort((a, b) => {
  const aDate = new Date(a?.[field] || a?.[fallbackField] || 0).getTime();
  const bDate = new Date(b?.[field] || b?.[fallbackField] || 0).getTime();
  return bDate - aDate;
});

function NoteBadge({ tone, children, icon }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${tone}`}>
      {icon}
      {children}
    </span>
  );
}

function Term({ label, value }) {
  return (
    <div className="rounded-sm border border-border p-4">
      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-mono text-xl font-black tabular-nums mt-1">{value}</p>
    </div>
  );
}

export default function NoteSiloDetail({ noteId, hid }) {
  const { bundle, tax1098, documents, loading, error, year, fmtCents, documentHref, reload } = useNoteDetail(noteId, hid);
  const [acqCost, setAcqCost] = useState("");
  const [acqUpb, setAcqUpb] = useState("");
  const [acqDate, setAcqDate] = useState("");
  const [acqSource, setAcqSource] = useState("");
  const [acqNotes, setAcqNotes] = useState("");
  const [savingAcq, setSavingAcq] = useState(false);

  if (loading) {
    return (
      <div data-testid="note-detail-loading" className="bg-card border border-border rounded-sm p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
          <Receipt size={14} /> Mortgage Note Detail
        </p>
        <p className="text-sm text-muted-foreground mt-4">Loading note details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="note-detail-error" className="bg-card border border-destructive/40 rounded-sm p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-destructive flex items-center gap-1.5">
          <WarningCircle size={14} weight="fill" /> Mortgage Note Detail
        </p>
        <p className="text-sm text-destructive/80 mt-4">{error}</p>
      </div>
    );
  }

  if (!bundle) {
    return (
      <div data-testid="note-detail-empty" className="bg-card border border-border rounded-sm p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
          <Receipt size={14} /> Mortgage Note Detail
        </p>
        <p className="text-sm text-muted-foreground mt-4">No note details returned.</p>
      </div>
    );
  }

  const statusKey = String(bundle.status || "").toLowerCase();
  const attentionKey = bundle.attention || "none";
  const attentionTone = ATTENTION_STYLES[attentionKey] || ATTENTION_STYLES.none;
  const statusTone = STATUS_STYLES[statusKey] || STATUS_STYLES.default;
  const daysLate = Number(bundle.computed_days_late || 0);
  const isLateAttention = String(attentionKey).startsWith("late_");
  const attentionText = isLateAttention && daysLate > 0
    ? `${daysLate} days late`
    : (ATTENTION_LABELS[attentionKey] || labelize(attentionKey));
  const payments = sortByDateDesc(bundle.payments, "payment_date", "received_date");
  const events = sortByDateDesc(bundle.events, "created_at");
  const docs = sortByDateDesc(documents, "doc_date");
  const taxPaymentCount = Array.isArray(tax1098?.payments) ? tax1098.payments.length : Number(tax1098?.payments || 0);
  const propertyAddress = [bundle.property_street, bundle.property_city, bundle.property_state].filter(Boolean).join(", ");
  const lenderIncome = bundle.lender_income;
  const lenderIncomeYears = Object.entries(lenderIncome?.by_year || {}).sort(([a], [b]) => String(a).localeCompare(String(b)));
  const schedule = bundle.schedule;
  const scheduleMonths = Array.isArray(schedule?.months) ? schedule.months : [];
  const showSchedule = schedule && scheduleMonths.length > 0;
  const costBasis = bundle.cost_basis;

  const saveAcquisition = async () => {
    const payload = {};
    const trimmedCost = acqCost.trim();
    const trimmedUpb = acqUpb.trim();
    const trimmedDate = acqDate.trim();
    const trimmedSource = acqSource.trim();
    const trimmedNotes = acqNotes.trim();

    if (trimmedCost) payload.acquisition_cost_cents = Math.round(parseFloat(trimmedCost) * 100);
    if (trimmedUpb) payload.acquisition_upb_cents = Math.round(parseFloat(trimmedUpb) * 100);
    if (trimmedDate) payload.acquisition_date = trimmedDate;
    if (trimmedSource) payload.acquisition_source = trimmedSource;
    if (trimmedNotes) payload.acquisition_notes = trimmedNotes;

    setSavingAcq(true);
    try {
      await api.patch(`/notes/${encodeURIComponent(noteId)}?household_id=${encodeURIComponent(hid)}`, payload);
      reload();
    } finally {
      setSavingAcq(false);
    }
  };

  return (
    <section data-testid="note-silo-detail" className="space-y-4">
      <div className="bg-card border border-border rounded-sm p-6 space-y-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
              <Receipt size={14} /> Mortgage Note Detail
            </p>
            <h2 className="font-display text-2xl font-black tracking-tighter mt-2">{bundle.borrower_full_name || "Borrower not named"}</h2>
            <p className="text-sm text-muted-foreground mt-1">{propertyAddress || "Property address not provided"}</p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <NoteBadge tone={statusTone} icon={<CheckCircle size={11} weight="fill" />}>{labelize(bundle.status || "status")}</NoteBadge>
              <NoteBadge tone={attentionTone} icon={attentionKey === "none" ? null : <WarningCircle size={11} weight="fill" />}>{attentionText}</NoteBadge>
              {bundle.stale_sync && (
                <NoteBadge tone="bg-[hsl(var(--ploy-attn-soft))] text-[hsl(var(--ploy-attn))]" icon={<WarningCircle size={11} weight="fill" />}>Stale sync</NoteBadge>
              )}
            </div>
          </div>
          {bundle.source && (
            <div className="rounded-sm border border-border p-3 min-w-[140px]">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Source</p>
              <p className="text-sm font-semibold mt-0.5">{labelize(bundle.source)}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Term label="Rate" value={fmtRate(bundle.note_rate)} />
          <Term label="Monthly Payment" value={fmtCents(bundle.total_payment_cents)} />
          <Term label="Next Due" value={fmtDate(bundle.next_due_date)} />
          <Term label="Maturity" value={fmtDate(bundle.maturity_date)} />
          <Term label="Unpaid Balance" value={fmtCents(bundle.current_balance_cents)} />
          <Term label="Original Balance" value={fmtCents(bundle.original_balance_cents)} />
          <Term label="Servicing Fee" value={fmtCents(bundle.monthly_servicing_fee_cents)} />
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-6 space-y-4">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Payment Ledger</p>
        {payments.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-sm">
            {bundle.source === "fci"
              ? "Serviced by FCI — payment history is maintained by the servicer and is not yet imported here."
              : "No payments recorded."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 font-bold">Date</th>
                  <th className="py-2 pr-3 font-bold text-right">Amount</th>
                  <th className="py-2 pr-3 font-bold text-right">Interest</th>
                  <th className="py-2 pr-3 font-bold text-right">Principal</th>
                  <th className="py-2 pr-3 font-bold text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, idx) => (
                  <tr key={`${p.payment_date || p.received_date || "payment"}-${idx}`} className="border-b border-border/60">
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <p className="font-mono text-xs">{fmtDate(p.payment_date || p.received_date)}</p>
                      {p.payment_method && <p className="text-[11px] text-muted-foreground mt-0.5">{labelize(p.payment_method)}</p>}
                      {p.memo && <p className="text-[11px] text-muted-foreground mt-0.5 max-w-[240px] truncate">{p.memo}</p>}
                    </td>
                    <td className="py-2 pr-3 font-mono tabular-nums text-right font-semibold">{fmtCents(p.amount_cents)}</td>
                    <td className="py-2 pr-3 font-mono tabular-nums text-right">{fmtCents(p.interest_cents)}</td>
                    <td className="py-2 pr-3 font-mono tabular-nums text-right">{fmtCents(p.principal_cents)}</td>
                    <td className="py-2 pr-3 font-mono tabular-nums text-right">{fmtCents(p.running_balance_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {lenderIncome && (
        <div data-testid="note-lender-income" className="bg-card border border-border rounded-sm p-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Lender Income (Schedule B)</p>
            <p className="text-xs text-muted-foreground max-w-xl">
              Lender-received interest is shown net of the servicing fee and is distinct from borrower-paid interest in the 1098 panel.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Term
              label="Lifetime Net Interest Received"
              value={<span data-testid="lender-lifetime-interest">{fmtCents(lenderIncome.lifetime_interest_cents)}</span>}
            />
            <Term label="Lifetime Servicing Fee" value={fmtCents(lenderIncome.lifetime_service_fee_cents)} />
          </div>
          {lenderIncomeYears.length > 0 && (
            <div className="rounded-sm border border-border divide-y divide-border">
              {lenderIncomeYears.map(([yr, cents]) => (
                <div key={yr} data-testid={`lender-year-${yr}`} className="flex items-center justify-between gap-3 p-3 text-sm">
                  <p className="font-bold">{yr}</p>
                  <p className="font-mono tabular-nums font-semibold">{fmtCents(cents)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showSchedule && (
        <div data-testid="note-sva-schedule" className="bg-card border border-border rounded-sm p-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Scheduled vs Actual</p>
              <p data-testid="sva-year-range" className="text-sm font-semibold mt-1">
                {schedule.from_year || "-"}-{schedule.to_year || "-"}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Term label="Interest Scheduled" value={fmtCents(schedule.total_interest_scheduled_cents)} />
            <Term label="Interest Received" value={fmtCents(schedule.total_interest_received_cents)} />
            <Term label="Principal Scheduled" value={fmtCents(schedule.total_principal_scheduled_cents)} />
            <Term label="Principal Received" value={fmtCents(schedule.total_principal_received_cents)} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 font-bold">Due Date</th>
                  <th className="py-2 pr-3 font-bold text-right">Interest Scheduled</th>
                  <th className="py-2 pr-3 font-bold text-right">Interest Received</th>
                  <th className="py-2 pr-3 font-bold text-right">Principal Scheduled</th>
                  <th className="py-2 pr-3 font-bold text-right">Principal Received</th>
                  <th className="py-2 pr-3 font-bold text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {scheduleMonths.map((month, idx) => (
                  <tr key={`${month.due_date || "schedule"}-${idx}`} data-testid={`sva-month-${idx}`} className="border-b border-border/60">
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <p className="font-mono text-xs">{fmtDate(month.due_date)}</p>
                      {month.received_date && <p className="text-[11px] text-muted-foreground mt-0.5">Received {fmtDate(month.received_date)}</p>}
                    </td>
                    <td className="py-2 pr-3 font-mono tabular-nums text-right">{fmtCents(month.interest_scheduled_cents)}</td>
                    <td className="py-2 pr-3 font-mono tabular-nums text-right">{fmtCents(month.interest_received_cents)}</td>
                    <td className="py-2 pr-3 font-mono tabular-nums text-right">{fmtCents(month.principal_scheduled_cents)}</td>
                    <td className="py-2 pr-3 font-mono tabular-nums text-right">{fmtCents(month.principal_received_cents)}</td>
                    <td className="py-2 pr-3 text-right">
                      <NoteBadge
                        tone={month.is_paid
                          ? "bg-[hsl(var(--ploy-pos-soft))] text-[hsl(var(--ploy-pos))]"
                          : "bg-[hsl(var(--ploy-attn-soft))] text-[hsl(var(--ploy-attn))]"}
                        icon={month.is_paid ? <CheckCircle size={11} weight="fill" /> : null}
                      >
                        {month.is_paid ? "Paid" : "Scheduled"}
                      </NoteBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {costBasis && (
        <div data-testid="note-cost-basis" className="bg-card border border-border rounded-sm p-6 space-y-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Cost Basis & Yield</p>
          {costBasis.has_cost_basis ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Term label="Yield on Cost" value={<span data-testid="cost-basis-yield">{fmtRatioPct(costBasis.yield_on_cost)}</span>} />
              <Term label="Discount Captured" value={<span data-testid="cost-basis-discount">{fmtRatioPct(costBasis.discount_captured)}</span>} />
              <Term label="Cost Basis" value={fmtCents(costBasis.cost_basis_cents)} />
              <Term label="Unrealized Gain" value={fmtCents(costBasis.unrealized_gain_cents)} />
            </div>
          ) : (
            <p data-testid="cost-basis-empty" className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-sm">
              Add acquisition data to calculate cost basis and yield.
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <label className="space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Acquisition Cost</span>
              <input
                data-testid="acq-cost-input"
                type="number"
                inputMode="decimal"
                value={acqCost}
                onChange={(e) => setAcqCost(e.target.value)}
                className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm font-mono tabular-nums"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Acquisition UPB</span>
              <input
                data-testid="acq-upb-input"
                type="number"
                inputMode="decimal"
                value={acqUpb}
                onChange={(e) => setAcqUpb(e.target.value)}
                className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm font-mono tabular-nums"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Acquisition Date</span>
              <input
                data-testid="acq-date-input"
                type="date"
                value={acqDate}
                onChange={(e) => setAcqDate(e.target.value)}
                className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Source</span>
              <input
                data-testid="acq-source-input"
                type="text"
                value={acqSource}
                onChange={(e) => setAcqSource(e.target.value)}
                className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Notes</span>
              <input
                data-testid="acq-notes-input"
                type="text"
                value={acqNotes}
                onChange={(e) => setAcqNotes(e.target.value)}
                className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
          </div>
          <button
            data-testid="acq-save"
            type="button"
            onClick={saveAcquisition}
            disabled={savingAcq}
            className="inline-flex items-center justify-center rounded-sm bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {savingAcq ? "Saving..." : "Save Acquisition"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-sm p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">1098 Summary</p>
            <NoteBadge
              tone={tax1098?.meets_600_threshold ? "bg-[hsl(var(--ploy-pos-soft))] text-[hsl(var(--ploy-pos))]" : "bg-[hsl(var(--ploy-neutral-soft))] text-[hsl(var(--ploy-neutral))]"}
              icon={tax1098?.meets_600_threshold ? <CheckCircle size={11} weight="fill" /> : null}
            >
              {tax1098?.meets_600_threshold ? "$600 threshold met" : "Below $600 threshold"}
            </NoteBadge>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Term label={`${year} Interest`} value={fmtCents(tax1098?.interest_cents)} />
            <Term label={`${year} Principal`} value={fmtCents(tax1098?.principal_cents)} />
          </div>
          <p className="text-xs text-muted-foreground">{taxPaymentCount} payment{taxPaymentCount === 1 ? "" : "s"} counted for {year}.</p>
        </div>

        <div className="bg-card border border-border rounded-sm p-6 space-y-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Documents</p>
          {docs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-sm">
              {bundle.source === "fci"
                ? "Serviced by FCI — the servicer maintains this loan file."
                : "No note documents returned."}
            </p>
          ) : (
            <div className="space-y-2">
              {docs.map((doc) => (
                <a
                  key={doc.id}
                  href={documentHref(doc.id)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`note-document-${doc.id}`}
                  className="rounded-sm border border-border p-3 flex items-center gap-3 hover:bg-secondary transition-colors"
                >
                  <FilePdf size={18} weight="fill" className="text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{doc.filename || "Document"}</p>
                    <p className="text-xs text-muted-foreground truncate">{labelize(doc.doc_type || "document")}{doc.doc_date ? ` - ${fmtDate(doc.doc_date)}` : ""}</p>
                  </div>
                  <ArrowSquareOut size={14} className="text-muted-foreground shrink-0" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-6 space-y-4">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Event Log</p>
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-sm">No note events recorded.</p>
        ) : (
          <div className="space-y-2">
            {events.map((event, idx) => (
              <div key={`${event.created_at || "event"}-${idx}`} className="rounded-sm border border-border p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-sm font-semibold">{labelize(event.event_type || "event")}</p>
                  <p className="text-xs text-muted-foreground font-mono">{fmtTimestamp(event.created_at)}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {event.actor ? `${event.actor} - ` : ""}
                  {typeof event.detail === "string"
                    ? event.detail
                    : event.summary
                      || (event.detail && Object.keys(event.detail).length
                          ? Object.entries(event.detail).map(([k, v]) => `${k}: ${String(v)}`).join(" · ")
                          : "No detail provided")}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
